import type { MDXMetadata } from 'mdx/types'

// Site constants
export const SITE_NAME = 'Beyond Cracking the Coding Interview'
export const SITE_NAME_SHORT = 'BCtCI'
export const SITE_NAME_TAB = 'Beyond CtCI'
export const BASE_URL = 'https://beyondctci.dev'
export const DEFAULT_DESCRIPTION =
  'Master advanced algorithms and data structures for technical interviews.'

/**
 * Traverses up the parent chain to find inherited values for metadata fields.
 * This allows lessons/units to inherit image, description, etc. from their parent course.
 */
export function getInheritedField<K extends keyof MDXMetadata>(
  page: MDXMetadata | undefined,
  field: K,
): MDXMetadata[K] | undefined {
  if (!page) return undefined
  if (page[field] !== undefined) return page[field]
  return getInheritedField(page.parent, field)
}

/**
 * Normalizes an image path and returns the static path.
 */
export function getStaticImagePath(imagePath: string): string {
  const normalizedPath = imagePath.startsWith('/')
    ? imagePath.slice(1)
    : imagePath
  return normalizedPath.startsWith('static/')
    ? `/${normalizedPath}`
    : `/static/${normalizedPath}`
}

/**
 * Generates an optimized image URL using Vercel's image optimization.
 * In production, uses /_vercel/image endpoint.
 * In development, serves directly from static folder.
 */
export function getOptimizedImageUrl(
  imagePath: string,
  width: number = 1200,
  quality: number = 80,
): string {
  const staticPath = getStaticImagePath(imagePath)

  // In production, use Vercel's image optimization
  if (process.env.NODE_ENV === 'production') {
    const params = new URLSearchParams({
      url: staticPath,
      w: width.toString(),
      q: quality.toString(),
    })
    return `/_vercel/image?${params.toString()}`
  }

  // In development, serve directly
  return staticPath
}

/**
 * Builds an absolute image URL for OG/Twitter meta tags.
 */
export function getAbsoluteImageUrl(imagePath: string): string {
  const optimizedPath = getOptimizedImageUrl(imagePath, 1200, 80)
  return `${BASE_URL}${optimizedPath}`
}

/**
 * Recursively counts units and lessons within a metadata node.
 * Traverses the entire tree to count all nested units and lessons.
 */
export function countUnitsAndLessons(node: MDXMetadata | undefined): {
  units: number
  lessons: number
} {
  if (!node?.children?.length) return { units: 0, lessons: 0 }

  let units = 0
  let lessons = 0

  for (const child of node.children) {
    if (child.type === 'unit') units += 1
    if (child.type === 'lesson') lessons += 1

    const nested = countUnitsAndLessons(child)
    units += nested.units
    lessons += nested.lessons
  }

  return { units, lessons }
}
